import type { Session } from "@/libs/auth/session";
import type { SessionValidationResult } from "@/libs/auth/utils";
import { queries } from "@/utils/query";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import type { UseSuspenseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

/**
 * A component that handles authentication state and renders different content based on the authentication status.
 *
 * This component utilizes React Suspense and ErrorBoundary to handle loading and error states
 * while checking the user's authentication status.
 *
 * @param props - Component props
 * @param props.children - Render function that receives the session result when authenticated
 * @param props.renderLoading - Function that returns content to display while loading authentication status
 * @param props.renderUnauthenticated - Optional function that returns content to display when user is not authenticated
 * @param props.renderError - Optional function that returns content to display when an error occurs
 *
 * @returns A React component that conditionally renders based on authentication state
 *
 * @example
 * ```tsx
 * <AuthLoader
 *   renderLoading={() => <LoadingSpinner />}
 *   renderUnauthenticated={() => <LoginPrompt />}
 *   renderError={(error) => <ErrorDisplay message={error.message} />}
 * >
 *   {(sessionResult) => <Dashboard user={sessionResult.data.session.user} />}
 * </AuthLoader>
 * ```
 */
export function AuthLoader({
  children,
  renderLoading,
  renderUnauthenticated = () => null,
  renderError = (error: Error) => error.message,
}: {
  children: (
    sessionResult: UseSuspenseQueryResult<
      Extract<SessionValidationResult, { session: Session }>,
      Error
    >,
  ) => ReactNode;
  renderLoading: () => ReactNode;
  renderUnauthenticated?: () => ReactNode;
  renderError?: (error: Error) => ReactNode;
}) {
  return (
    <ErrorBoundary fallback={({ error }) => renderError(error)}>
      <Suspense fallback={renderLoading()}>
        <SuspenseQuery {...queries.auth.getCurrentSession.getOptions()}>
          {(result) => {
            return result.data.session
              ? children(
                  result as UseSuspenseQueryResult<
                    Extract<SessionValidationResult, { session: Session }>,
                    Error
                  >,
                )
              : renderUnauthenticated();
          }}
        </SuspenseQuery>
      </Suspense>
    </ErrorBoundary>
  );
}
